/**
 * Mapping from method enum values to display names
 * This preserves slashes and proper capitalization
 */
const METHOD_DISPLAY_MAP = {
  'open die/hand forging': 'Open Die/Hand Forging',
  'closed die forging': 'Closed Die Forging',
  'cold forging': 'Cold Forging',
  'upset forging': 'Upset Forging',
  'investment casting': 'Investment Casting',
  'sand casting': 'Sand Casting',
  'die casting': 'Die Casting',
  'permanent mold casting': 'Permanent Mold Casting',
  'semi permanent mold casting': 'Semi Permanent Mold Casting',
  'centrifugal casting': 'Centrifugal Casting',
  'plaster casting': 'Plaster Casting',
  'rolled ring': 'Rolled Ring',
  'other methods as specified': 'Other Methods As Specified',
  'casting': 'Casting',
  'forging': 'Forging',
  'unspecified': 'Unspecified',
  'none': 'None',
};

/**
 * Converts camelCase to space-separated lowercase string
 * @param {string} camelCase - The camelCase string (e.g., "openDieHandForcing")
 * @returns {string} - The converted string (e.g., "open die hand forcing")
 */
// Handle camelCase: insert space before capital letters, then lowercase
const camelCaseToEnumFormat = (camelCase) =>
  camelCase
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between lowercase and uppercase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle consecutive capitals
    .toLowerCase()
    .trim();

/**
 * Formats a method value for display, preserving slashes and proper capitalization
 * @param {string} value - The method value (e.g., "open die/hand forging", "openDieHandForcing")
 * @returns {string} - The formatted display value (e.g., "Open Die/Hand Forging")
 */
export const formatMethodValue = (value) => {
  if (!value || typeof value !== 'string') {
    return value;
  }
  
  // Trim whitespace
  const trimmedValue = value.trim();
  
  let lowerValue;
  
  // Handle camelCase IDs like "openDieHandForcing" or "openDieHandForging"
  // Convert to enum format first BEFORE lowercasing
  if (/[A-Z]/.test(trimmedValue)) {
    lowerValue = camelCaseToEnumFormat(trimmedValue);
  } else {
    lowerValue = trimmedValue.toLowerCase();
  }
  
  // Normalize: handle cases where there's no space between words
  // e.g., "opendiehandforging" -> "open die hand forging"
  if (!lowerValue.includes(' ') && lowerValue.length > 10) {
    // Try to split camelCase-like patterns even if already lowercase
    lowerValue = lowerValue
      .replace(/(die)(hand)/gi, '$1 $2')
      .replace(/(hand)(forg)/gi, '$1 $2')
      .replace(/(closed)(die)/gi, '$1 $2')
      .replace(/(cold)(forg)/gi, '$1 $2')
      .replace(/(upset)(forg)/gi, '$1 $2');
  }
  
  // Check if we have a mapping for this value
  if (METHOD_DISPLAY_MAP[lowerValue]) {
    return METHOD_DISPLAY_MAP[lowerValue];
  }
  
  // Handle case where value might already be "Open Die Hand Forging" (without slash)
  // or "Open Die Hand Forcing" (typo) and map it to "Open Die/Hand Forging"
  if (lowerValue === 'open die hand forging' || 
      lowerValue === 'open die hand forcing' ||
      lowerValue === 'open die hand forg ing' ||
      lowerValue === 'open die hand forc ing') {
    return 'Open Die/Hand Forging';
  }
  
  // Handle camelCase variations that might have been converted incorrectly
  if (lowerValue.includes('open') && lowerValue.includes('die') && 
      (lowerValue.includes('hand') || lowerValue.includes('forg'))) {
    return 'Open Die/Hand Forging';
  }
  
  // Return original value if no mapping found
  return trimmedValue;
};

/**
 * Formats an array of method values for display
 * @param {string[]} valuesArray - Array of method values
 * @returns {string[]} - Array of formatted display values
 */
export const formatMethodValuesArray = (valuesArray) => {
  if (!Array.isArray(valuesArray)) {
    return valuesArray;
  }
  
  return valuesArray.map(val => formatMethodValue(val));
};

/**
 * Reverse mapping from display names back to enum values
 * Used when sending data to the server
 */
const DISPLAY_TO_ENUM_MAP = {
  'Open Die/Hand Forging': 'open die/hand forging',
  'Closed Die Forging': 'closed die forging',
  'Cold Forging': 'cold forging',
  'Upset Forging': 'upset forging',
  'Investment Casting': 'investment casting',
  'Sand Casting': 'sand casting',
  'Die Casting': 'die casting',
  'Permanent Mold Casting': 'permanent mold casting',
  'Semi Permanent Mold Casting': 'semi permanent mold casting',
  'Centrifugal Casting': 'centrifugal casting',
  'Plaster Casting': 'plaster casting',
  'Rolled Ring': 'rolled ring',
  'Other Methods As Specified': 'other methods as specified',
  'Casting': 'casting',
  'Forging': 'forging',
  'Unspecified': 'unspecified',
  'None': 'none',
};

/**
 * Converts display format back to enum format for server communication
 * @param {string} displayValue - The display value (e.g., "Open Die/Hand Forging")
 * @returns {string} - The enum value (e.g., "open die/hand forging")
 */
export const displayToEnumFormat = (displayValue) => {
  if (!displayValue || typeof displayValue !== 'string') {
    return displayValue;
  }
  
  return DISPLAY_TO_ENUM_MAP[displayValue] || displayValue.toLowerCase();
};

export default formatMethodValue;



// WEBPACK FOOTER //
// ./src/utils/methodFormatter.js